import PropTypes from "prop-types";

import { BitmapLayer } from "@deck.gl/layers";
import { TileLayer } from "@deck.gl/geo-layers";

import { fcbmModule, gsModule, scbmModule } from "./orthoShadersModules.ts";

const TILE_SIZE = 512;

// TODO: Consider combining all shaders into one layer with injected code, to save on needing to repull tiles
class FiveColorBitmapLayer extends BitmapLayer {
	getShaders() {
		const shaders = super.getShaders();
		return {
			...shaders,
			modules: [...shaders.modules, fcbmModule],
			inject: {
				"fs:DECKGL_FILTER_COLOR": `
						if (
							color.a == 0.0 || 
							(color.rgb == vec3(0.0, 0.0, 0.0) && !fcbm.inverse) ||
							(color.rgb == vec3(1.0, 1.0, 1.0) && fcbm.inverse)
						) {
							discard;
						}

						vec4 maxColor = vec4(vec3(fcbm.max), 0);
						vec4 minColor = vec4(vec3(fcbm.min), 0);

						vec4 range = maxColor - minColor;
						vec4 modifiedColor =  vec4(((vec4(clamp(color.rgb, fcbm.min, fcbm.max), 1) - minColor) / range).rgb, 1);
						
						float grayscale = dot(modifiedColor.rgb, vec3(0.299, 0.587, 0.114));

						vec4 newColor = mix(fcbm.color1, fcbm.color2, smoothstep(0.0, 0.25, grayscale));
						newColor = mix(newColor, fcbm.color3, smoothstep(0.25, 0.5, grayscale));
						newColor = mix(newColor, fcbm.color4, smoothstep(0.5, 0.75, grayscale));
						color.rgb = mix(newColor, fcbm.color5, smoothstep(0.75, 1.0, grayscale)).rgb;
						
						`,
			},
		};
	}

	draw(opts) {
		const { colorRange, min, max, inverse } = this.props;

		const fcbmProps = {
			color1: colorRange[0].map((x) => x / 255),
			color2: colorRange[1].map((x) => x / 255),
			color3: colorRange[2].map((x) => x / 255),
			color4: colorRange[3].map((x) => x / 255),
			color5: colorRange[4].map((x) => x / 255),
			min: Number.parseFloat(min),
			max: Number.parseFloat(max),
			inverse: inverse,
		};

		this.state.model.shaderInputs.setProps({ fcbm: fcbmProps });
		super.draw(opts);
	}
}

class SevenColorBitmapLayer extends BitmapLayer {
	getShaders() {
		const shaders = super.getShaders();
		return {
			...shaders,
			modules: [...shaders.modules, scbmModule],
			inject: {
				"fs:DECKGL_FILTER_COLOR": `
						if (
							color.a == 0.0 || 
							(color.rgb == vec3(0.0, 0.0, 0.0) && !scbm.inverse) ||
							(color.rgb == vec3(1.0, 1.0, 1.0) && scbm.inverse)
						) {
							discard;
						}

						vec4 maxColor = vec4(vec3(scbm.max), 0);
						vec4 minColor = vec4(vec3(scbm.min), 0);

						vec4 range = maxColor - minColor;
						vec4 modifiedColor = vec4(((vec4(clamp(color.rgb, scbm.min, scbm.max), 1) - minColor) / range).rgb, 1);

						float grayscale = dot(modifiedColor.rgb, vec3(0.299, 0.587, 0.114));
						
						vec4 newColor = mix(scbm.color1, scbm.color2, smoothstep(0.0, 0.167, grayscale));
						newColor = mix(newColor, scbm.color3, smoothstep(0.167, 0.33, grayscale));
						newColor = mix(newColor, scbm.color4, smoothstep(0.33, 0.5, grayscale));
						newColor = mix(newColor, scbm.color5, smoothstep(0.5, 0.67, grayscale));
						newColor = mix(newColor, scbm.color6, smoothstep(0.67, 0.837, grayscale));
						color.rgb = mix(newColor, scbm.color7, smoothstep(0.837, 1.0, grayscale)).rgb;
						
					`,
			},
		};
	}

	draw(opts) {
		const { colorRange, min, max, inverse } = this.props;

		const scbmProps = {
			color1: colorRange[0].map((x) => x / 255),
			color2: colorRange[1].map((x) => x / 255),
			color3: colorRange[2].map((x) => x / 255),
			color4: colorRange[3].map((x) => x / 255),
			color5: colorRange[4].map((x) => x / 255),
			color6: colorRange[5].map((x) => x / 255),
			color7: colorRange[6].map((x) => x / 255),
			min: Number.parseFloat(min),
			max: Number.parseFloat(max),
			inverse: inverse,
		};

		this.state.model.shaderInputs.setProps({ scbm: scbmProps });
		super.draw(opts);
	}
}

class ClampedBitmapLayer extends BitmapLayer {
	getShaders() {
		const shaders = super.getShaders();
		return {
			...shaders,
			modules: [...shaders.modules, gsModule],
			inject: {
				"fs:DECKGL_FILTER_COLOR": `
						if (
							color.a == 0.0 ||
							(color.rgb == vec3(0.0, 0.0, 0.0) && !gs.inverse) ||
							(color.rgb == vec3(1.0, 1.0, 1.0) && gs.inverse)
						) {
							discard;
						}

						vec4 maxColor = vec4(vec3(gs.max), 0);
						vec4 minColor = vec4(vec3(gs.min), 0);

						vec4 range = maxColor - minColor;
						vec4 clampedColor = vec4(((vec4(clamp(color.rgb, gs.min, gs.max), 1) - minColor) / range).rgb, 1);

						color.rgb = clampedColor.rbg;
						
					`,
			},
		};
	}

	draw(opts) {
		const { min, max, inverse } = this.props;

		const gsProps = {
			min: Number.parseFloat(min),
			max: Number.parseFloat(max),
			inverse: inverse,
		};

		this.state.model.shaderInputs.setProps({ gs: gsProps });
		super.draw(opts);
	}
}

class BaseTileLayer extends TileLayer {
	constructor({ data, renderSubLayers }) {
		super({
			data: data.tileUrl,
			tileSize: TILE_SIZE,
			id: data.layerId,
			minZoom: 0,
			maxZoom: 26,
			pickable: true,
			maxRequests: 0,
			maxCacheSize: 1000000,
			beforeId: data.beforeId,
			updateTriggers: {
				renderSubLayers: [data.min, data.max, data.tileUrl, data.opacity],
			},
			renderSubLayers: renderSubLayers,
		});
	}
}

export class RYGLayer extends BaseTileLayer {
	static layerName = "RYGLayer";

	constructor({ data }) {
		super({
			data,
			renderSubLayers: (props) => {
				const {
					bbox: { west, south, east, north },
				} = props.tile;

				return new FiveColorBitmapLayer(props, {
					// id: `shader-ryg-${crypto.randomUUID()}`,
					data: null,
					image: props.data,
					bounds: [west, south, east, north],
					opacity: data.opacity,
					colorRange: [
						[128, 0, 0, 255],
						[255, 0, 0, 255],
						[255, 255, 0, 255],
						[0, 255, 0, 255],
						[0, 128, 0, 255],
					],
					min: data.min,
					max: data.max,
					inverse: data.inverse,
				});
			},
		});
	}
}

RYGLayer.propTypes = {
	data: PropTypes.object.isRequired,
};

export class ThermalLayer extends BaseTileLayer {
	static layerName = "GreenFireBlueLayer";

	constructor({ data }) {
		super({
			data,
			renderSubLayers: (props) => {
				const {
					bbox: { west, south, east, north },
				} = props.tile;

				return new FiveColorBitmapLayer(props, {
					// id: `shader-thermal-${crypto.randomUUID()}`,
					data: null,
					image: props.data,
					bounds: [west, south, east, north],
					opacity: data.opacity,
					colorRange: [
						[0, 0, 69, 255],
						[69, 0, 128, 255],
						[255, 0, 0, 255],
						[255, 255, 0, 255],
						[255, 255, 255, 255],
					],
					min: data.min,
					max: data.max,
					inverse: data.inverse,
				});
			},
		});
	}
}

ThermalLayer.propTypes = {
	data: PropTypes.object.isRequired,
};

export class GreenFireBlueLayer extends BaseTileLayer {
	static layerName = "GreenFireBlueLayer";

	constructor({ data }) {
		super({
			data,
			renderSubLayers: (props) => {
				const {
					bbox: { west, south, east, north },
				} = props.tile;

				return new FiveColorBitmapLayer(props, {
					// id: `shader-gfb-${crypto.randomUUID()}`,
					data: null,
					image: props.data,
					bounds: [west, south, east, north],
					opacity: data.opacity,
					colorRange: [
						[0, 0, 0, 255],
						[0, 0, 255, 255],
						[0, 255, 0, 255],
						[255, 255, 0, 255],
						[255, 255, 255, 255],
					],
					min: data.min,
					max: data.max,
					inverse: data.inverse,
				});
			},
		});
	}
}

GreenFireBlueLayer.propTypes = {
	data: PropTypes.object.isRequired,
};

export class SpectralLayer extends BaseTileLayer {
	static layerName = "SpectralLayer";

	constructor({ data }) {
		super({
			data,
			renderSubLayers: (props) => {
				const {
					bbox: { west, south, east, north },
				} = props.tile;

				return new SevenColorBitmapLayer(props, {
					// id: `shader-spectral-${crypto.randomUUID()}`,
					data: null,
					image: props.data,
					bounds: [west, south, east, north],
					opacity: data.opacity,
					colorRange: [
						[117, 0, 0, 255],
						[209, 0, 0, 255],
						[212, 130, 0, 255],
						[255, 255, 0, 255],
						[77, 255, 0, 255],
						[0, 255, 247, 255],
						[0, 17, 255, 255],
					],
					min: data.min,
					max: data.max,
					inverse: data.inverse,
				});
			},
		});
	}
}

SpectralLayer.propTypes = {
	data: PropTypes.object.isRequired,
};

export class BlueYellowLayer extends BaseTileLayer {
	static layerName = "BlueYellowLayer";

	constructor({ data }) {
		super({
			data,
			renderSubLayers: (props) => {
				const {
					bbox: { west, south, east, north },
				} = props.tile;

				return new SevenColorBitmapLayer(props, {
					// id: `shader-by-${crypto.randomUUID()}`,
					data: null,
					image: props.data,
					bounds: [west, south, east, north],
					opacity: data.opacity ?? 1,
					colorRange: [
						[0, 0, 255, 255],
						[157, 2, 215, 255],
						[205, 52, 181, 255],
						[234, 95, 148, 255],
						[250, 135, 117, 255],
						[255, 177, 78, 255],
						[255, 215, 0, 255],
					],
					min: data.min,
					max: data.max,
					inverse: data.inverse,
				});
			},
		});
	}
}

BlueYellowLayer.propTypes = {
	data: PropTypes.object.isRequired,
};

export class ClampedLayer extends BaseTileLayer {
	static layerName = "ClampedLayer";

	constructor({ data }) {
		super({
			data,
			renderSubLayers: (props) => {
				const {
					bbox: { west, south, east, north },
				} = props.tile;

				return new ClampedBitmapLayer(props, {
					// id: `shader-clamped-${crypto.randomUUID()}`,
					data: null,
					image: props.data,
					bounds: [west, south, east, north],
					opacity: data.opacity,
					min: data.min,
					max: data.max,
					inverse: data.inverse,
				});
			},
		});
	}
}

ClampedLayer.propTypes = {
	data: PropTypes.object.isRequired,
};
